import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { likeTanzaku } from '../services/api';
import { getFontById, DEFAULT_FONT } from '../utils/fonts';
import './TanzakuCard.css';

const TanzakuCard = ({ tanzaku, onClick, onLiked, onPositionChange, isDraggable = true }) => {
  const [isLiking, setIsLiking] = useState(false);
  const [likeError, setLikeError] = useState('');
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  
  // フォント情報を取得（フォールバック付き）
  const selectedFont = getFontById(tanzaku.font_id || DEFAULT_FONT.id);

  const handleLike = async (e) => {
    e.stopPropagation();
    
    if (isLiking) return;
    
    setIsLiking(true);
    setLikeError('');
    
    try {
      const updatedTanzaku = await likeTanzaku(tanzaku.id);
      onLiked(updatedTanzaku);
      
      // いいねアニメーション
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 1000);
      
    } catch (err) {
      setLikeError('いいねできませんでした');
      setTimeout(() => setLikeError(''), 3000);
    } finally {
      setIsLiking(false);
    }
  };

  const getColorClass = (color) => {
    return `tanzaku-${color}`;
  };

  const truncateText = (text, maxLength = 25) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '今';
    if (diffInHours < 24) return `${diffInHours}時間前`;
    if (diffInHours < 24 * 7) return `${Math.floor(diffInHours / 24)}日前`;
    return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
  };

  const handleDragEnd = (event, info) => {
    if (onPositionChange && isDraggable) {
      const currentElement = event.target.closest('.tanzaku-card');
      const container = document.querySelector('.tanzaku-grid');
      
      if (container && currentElement) {
        // ドラッグ終了時の要素の実際の位置を取得
        requestAnimationFrame(() => {
          const containerRect = container.getBoundingClientRect();
          const elementRect = currentElement.getBoundingClientRect();
          
          // 短冊の相対位置を計算
          const tanzakuLeftX = elementRect.left - containerRect.left;
          const tanzakuTopY = elementRect.top - containerRect.top;
          
          // パーセンテージに変換
          const newX = Math.max(0, Math.min(100, (tanzakuLeftX / containerRect.width) * 100));
          const newY = Math.max(0, Math.min(100, (tanzakuTopY / containerRect.height) * 100));
          
          // すべてのアニメーションを強制停止
          currentElement.style.transform = '';
          currentElement.style.transition = 'none';
          
          // 位置を即座に確定
          onPositionChange(tanzaku.id, { x: newX, y: newY });
          
          // 次フレームでtransitionを復元
          requestAnimationFrame(() => {
            currentElement.style.transition = '';
          });
        });
      }
    }
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    onClick(tanzaku);
  };

  return (
    <motion.div
      className={`tanzaku-card ${getColorClass(tanzaku.color)}`}
      drag={isDraggable}
      dragMomentum={false}
      dragElastic={0}
      dragTransition={{ power: 0, timeConstant: 0, min: 0, max: 0 }}
      dragPropagation={false}
      dragSnapToOrigin={false}
      onDragEnd={handleDragEnd}
      initial={false}
      layout={false}
      layoutId={false}
      whileHover={{ 
        scale: 1.05,
        rotate: Math.random() * 4 - 2,
        zIndex: 100,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
      whileDrag={{ 
        scale: 1.05,
        zIndex: 1000,
        rotate: 2,
        transition: { duration: 0 }
      }}
      animate={{
        rotate: [0, 1, -1, 0],
        y: [0, -2, 0]
      }}
      transition={{
        default: { type: "tween", duration: 0 },
        rotate: {
          duration: 4 + Math.random() * 2,
          repeat: Infinity,
          ease: "easeInOut"
        },
        y: {
          duration: 3 + Math.random() * 2,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }}
    >
      <div className="tanzaku-content">
        <div className="tanzaku-header">
          <div 
            className="author-name" 
            style={{ fontFamily: selectedFont.fontFamily }}
          >
            {tanzaku.author_name}
          </div>
          <div className="tanzaku-date">{formatDate(tanzaku.created_at)}</div>
        </div>
        
        <div 
          className="wish-text"
          style={{ fontFamily: selectedFont.fontFamily }}
        >
          {truncateText(tanzaku.wish_text)}
        </div>
        
        <div className="tanzaku-footer">
          <div className="footer-buttons">
            <motion.button
              className={`like-button ${isLiking ? 'liking' : ''}`}
              onClick={handleLike}
              disabled={isLiking}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="いいね"
            >
              <span className="heart">♥</span>
              <span className="like-count">{tanzaku.likes || 0}</span>
            </motion.button>
            
            <motion.button
              className="expand-button"
              onClick={handleExpandClick}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="詳細を見る"
            >
              <span className="expand-icon">🔍</span>
            </motion.button>
          </div>
          
          {showLikeAnimation && (
            <motion.div
              className="like-animation"
              initial={{ opacity: 0, scale: 0.5, y: 0 }}
              animate={{ opacity: 1, scale: 1.2, y: -30 }}
              exit={{ opacity: 0, scale: 0.5, y: -50 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              +1 ♥
            </motion.div>
          )}
        </div>
        
        {likeError && (
          <div className="like-error">{likeError}</div>
        )}
      </div>
      
      <div className="tanzaku-hole"></div>
      <div className="tanzaku-string"></div>
    </motion.div>
  );
};

export default TanzakuCard;